import React from 'react';
import { useTheme } from '../context/ThemeContext';
import styles from './CommercialDisclosure.module.css';

interface DisclosureItem {
  label: string;
  labelJa: string;
  value: React.ReactNode;
}

const disclosureItems: DisclosureItem[] = [
  {
    label: 'Legal Name',
    labelJa: '販売業者',
    value: 'Michael Farrelly',
  },
  {
    label: 'Address',
    labelJa: '所在地',
    value: '4-13-2-406 Tamagawa, Setagaya-ku, Tokyo 〒158-0094, Japan',
  },
  {
    label: 'Phone Number',
    labelJa: '電話番号',
    value: (
      <>
        080-5866-6395
        <span className={styles.subtext}>
          Hours: Weekdays 10:00–18:00 (excluding weekends and public holidays)
        </span>
      </>
    ),
  },
  {
    label: 'Email Address',
    labelJa: 'メールアドレス',
    value: (
      <a href="mailto:simplicitydevs@gmail.com" className={styles.link}>
        simplicitydevs@gmail.com
      </a>
    ),
  },
  {
    label: 'Head of Operations',
    labelJa: '運営責任者',
    value: 'Michael Farrelly',
  },
  {
    label: 'Price',
    labelJa: '販売価格',
    value: (
      <>
        Custom quotes — pricing varies by project scope and requirements.
        <span className={styles.subtext}>
          All prices are communicated in a written proposal prior to the start of any work.
        </span>
      </>
    ),
  },
  {
    label: 'Additional Fees',
    labelJa: '追加費用',
    value: 'None. All applicable fees are included in the project quote. No hidden charges.',
  },
  {
    label: 'Payment Methods',
    labelJa: '支払方法',
    value: 'Credit card via Stripe',
  },
  {
    label: 'Payment Period',
    labelJa: '支払期限',
    value: (
      <>
        Payment is due within 14 days of the invoice date.
        <span className={styles.subtext}>
          For project-based engagements, a deposit may be required prior to commencement of work,
          as specified in the project proposal.
        </span>
      </>
    ),
  },
  {
    label: 'Delivery Times',
    labelJa: '引渡し時期',
    value: (
      <>
        Delivery timelines are agreed upon at the start of each project and detailed in the
        statement of work (SOW).
        <span className={styles.subtext}>
          Digital deliverables (code, design assets, documentation) are provided via agreed
          platforms (e.g. GitHub, email) upon completion of each milestone.
        </span>
      </>
    ),
  },
  {
    label: 'Exchanges & Returns Policy',
    labelJa: '返品・交換について',
    value: (
      <>
        <strong>For defective or non-conforming deliverables:</strong>
        <span className={styles.subtext}>
          If the delivered work does not meet the agreed specifications, revisions will be
          provided at no additional cost. Please contact{' '}
          <a href="mailto:simplicitydevs@gmail.com" className={styles.link}>
            simplicitydevs@gmail.com
          </a>{' '}
          within 14 days of delivery.
        </span>
        <strong>For change-of-mind or non-defective cancellations:</strong>
        <span className={styles.subtext}>
          Deposits for work that has not yet commenced are refundable within 7 days of payment.
          Completed milestones are non-refundable. Partial refunds may be issued for
          work-in-progress at the discretion of Michael Farrelly.
        </span>
      </>
    ),
  },
];

const CommercialDisclosure: React.FC = () => {
  const { isDark } = useTheme();

  return (
    <div
      className={styles.pageWrapper}
      data-theme={isDark ? 'dark' : 'light'}
    >
      <div className={styles.pageHeader}>
        <h1>Commercial Disclosure</h1>
        <p className={styles.titleJa}>特定商取引法に基づく表記</p>
        <p className={styles.subtitle}>
          The following information is provided in accordance with Japan's Specified Commercial
          Transactions Act (特定商取引法) and as required by Stripe for payment processing.
        </p>
      </div>

      <div className={styles.disclosureCard}>
        <dl className={styles.disclosureList}>
          {disclosureItems.map((item, index) => (
            <div
              key={item.label}
              className={`${styles.disclosureRow} ${index % 2 === 0 ? styles.rowEven : ''}`}
            >
              <dt className={styles.disclosureLabel}>
                <span className={styles.labelEn}>{item.label}</span>
                <span className={styles.labelJa}>{item.labelJa}</span>
              </dt>
              <dd className={styles.disclosureValue}>{item.value}</dd>
            </div>
          ))}
        </dl>
      </div>

      <p className={styles.lastUpdated}>Last updated: March 1, 2026</p>
    </div>
  );
};

export default CommercialDisclosure;
